import React, { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';

import { ScheduleEntry } from 'us-web-services';
import { useSharedTranslation } from '../../hooks/useSharedTranslation';
import { Button, ButtonType } from '../../Button/Button';
import {
  HOUR_HEIGHT,
  SLOT_MINUTES,
  bookingStatusKey,
  formatTime,
  isSameDay,
  minutesSinceMidnight,
  parseApiDate,
  pxFromMinutes,
  snapToSlot,
  toDateKey,
} from '../utils';

interface Props {
  date: Date;
  entries: ScheduleEntry[];
  canEdit: boolean;
  onCreate: (start: Date, end: Date) => void;
  onEditAvailability: (entry: ScheduleEntry) => void;
  onOpenBooking: (entry: ScheduleEntry) => void;
  now?: Date;
  namespace?: string;
  showBookingDetails?: boolean;
}

const yToDate = (day: Date, y: number): Date => {
  const totalMinutes = (y / HOUR_HEIGHT) * 60;
  const clamped = Math.max(0, Math.min(24 * 60, totalMinutes));
  const out = new Date(day);
  out.setHours(0, 0, 0, 0);
  out.setMinutes(Math.floor(clamped));
  return snapToSlot(out);
};

export const TimeGridColumn = ({
  date,
  entries,
  canEdit,
  onCreate,
  onEditAvailability,
  onOpenBooking,
  now,
  namespace,
  showBookingDetails,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);
  const columnRef = useRef<HTMLDivElement | null>(null);
  const [dragStart, setDragStart] = useState<Date | null>(null);
  const [dragEnd, setDragEnd] = useState<Date | null>(null);

  const positioned = useMemo(() => {
    const items: Array<{
      entry: ScheduleEntry;
      top: number;
      height: number;
    }> = [];
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    for (const entry of entries) {
      if (!entry.start || !entry.end) continue;
      const s = parseApiDate(entry.start);
      const e = parseApiDate(entry.end);
      if (!s || !e) continue;
      if (!(s < dayEnd && e > dayStart)) continue;
      const effStart = s < dayStart ? dayStart : s;
      const effEnd = e > dayEnd ? dayEnd : e;
      const startMins = Math.max(
        0,
        (effStart.getTime() - dayStart.getTime()) / 60000,
      );
      const endMins = Math.min(
        24 * 60,
        (effEnd.getTime() - dayStart.getTime()) / 60000,
      );
      const top = pxFromMinutes(startMins);
      const height = Math.max(16, pxFromMinutes(endMins - startMins));
      items.push({ entry, top, height });
    }
    return items;
  }, [entries, date]);

  useEffect(() => {
    if (!dragStart) return;
    const onMove = (e: MouseEvent | globalThis.MouseEvent): void => {
      if (!columnRef.current) return;
      const rect = columnRef.current.getBoundingClientRect();
      const y = (e as globalThis.MouseEvent).clientY - rect.top;
      setDragEnd(yToDate(date, y));
    };
    const onUp = (): void => {
      if (dragStart && dragEnd) {
        const [s, e] = dragStart < dragEnd ? [dragStart, dragEnd] : [dragEnd, dragStart];
        const end = new Date(e);
        if (end.getTime() - s.getTime() < SLOT_MINUTES * 60 * 1000) {
          end.setMinutes(end.getMinutes() + 60);
        }
        onCreate(s, end);
      }
      setDragStart(null);
      setDragEnd(null);
    };
    document.addEventListener('mousemove', onMove as unknown as EventListener);
    document.addEventListener('mouseup', onUp);
    return (): void => {
      document.removeEventListener('mousemove', onMove as unknown as EventListener);
      document.removeEventListener('mouseup', onUp);
    };

  }, [dragStart, dragEnd]);

  const startDrag = (e: MouseEvent<HTMLDivElement>): void => {
    if (!canEdit) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-pill]')) return;
    if (!columnRef.current) return;
    const rect = columnRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const snapped = yToDate(date, y);
    setDragStart(snapped);
    setDragEnd(snapped);
    e.preventDefault();
  };

  const todayMinutes =
    now && isSameDay(now, date) ? minutesSinceMidnight(now) : -1;

  const dragBand = dragStart && dragEnd
    ? ((): { top: number; height: number } => {
        const [s, e] = dragStart < dragEnd ? [dragStart, dragEnd] : [dragEnd, dragStart];
        const top = pxFromMinutes(minutesSinceMidnight(s));
        const height = Math.max(8, pxFromMinutes(minutesSinceMidnight(e) - minutesSinceMidnight(s)));
        return { top, height };
      })()
    : null;

  const renderBookingDetails = showBookingDetails ?? canEdit;

  return (
    <div
      ref={columnRef}
      className='relative border-l border-neutral-200'
      style={{ height: HOUR_HEIGHT * 24 }}
      onMouseDown={startDrag}
      data-date={toDateKey(date)}
      role='button'
      tabIndex={0}
      aria-label={t('pages.calendar.timeGridAria')}
    >
      {Array.from({ length: 24 }, (_, i) => (
        <div
          key={i}
          className='absolute left-0 right-0 border-t border-neutral-100'
          style={{ top: i * HOUR_HEIGHT }}
        />
      ))}

      {positioned.map(({ entry, top, height }) => {
        const isBooking = !!entry.booking;
        const pending =
          entry.booking?.status?.code === 'pending' ||
          entry.booking?.status?.code === 'parent_booking' ||
          entry.booking?.status?.code === 'parent_reschedule' ||
          entry.booking?.status?.code === 'sitter_reschedule';
        const cls = isBooking
          ? pending
            ? 'bg-honey-50 border-t-4 border-honey-500 !text-neutral-800 hover:bg-honey-300'
            : 'bg-sapphire-50 border-t-4 border-sapphire !text-neutral-800 hover:bg-sapphire-100'
          : 'bg-emerald-100 border-t-4 border-emerald-500 !text-emerald-900 hover:bg-emerald-200';
        const parent = entry.booking?.parent;
        const firstName = parent?.firstName ?? parent?.name ?? t('pages.calendar.booking');
        const lastInitial = parent?.lastName
          ? `${parent.lastName.charAt(0)}.`
          : '';
        const address = entry.booking?.address;
        const timeLine = t('common.timeRange', {
          start: formatTime(entry.start),
          end: entry.pastMidnight ? t('pages.calendar.midnightPlus') : formatTime(entry.end),
        });
        return (
          <Button
            data-pill
            key={entry.id ?? entry.start}
            buttonType={ButtonType.tertiary}
            onClick={(e): void => {
              e.stopPropagation();
              if (entry.booking) onOpenBooking(entry);
              else onEditAvailability(entry);
            }}
            className={`absolute left-0.5 right-0.5 !min-w-0 !rounded !block text-xs text-left !p-1 overflow-hidden ${cls}`}
            style={{ top, height }}
            title={t('common.timeRange', { start: formatTime(entry.start), end: formatTime(entry.end) })}
          >
            {isBooking ? (
              renderBookingDetails ? (
                <div className='text-center leading-tight w-full'>
                  <div className='font-bold truncate'>
                    {t(bookingStatusKey(entry.booking))}
                  </div>
                  {height >= 28 && (
                    <div className='text-[10px] opacity-90 truncate'>
                      {timeLine}
                    </div>
                  )}
                  {height >= 48 && parent?.pictureUrl && (
                    <img
                      alt=''
                      src={parent.pictureUrl}
                      className='w-5 h-5 rounded-full object-cover mx-auto mt-0.5'
                    />
                  )}
                  {height >= 48 && (
                    <div className='font-bold text-[10px] truncate'>
                      {firstName} {lastInitial}
                    </div>
                  )}
                  {height >= 64 && address?.city && address?.state && (
                    <div className='text-[10px] opacity-80 truncate'>
                      {address.city}, {address.state}
                    </div>
                  )}
                  {height >= 84 && (
                    <div className='text-[10px] opacity-80 truncate'>
                      {t('pages.calendar.ratePerHour', { rate: entry.booking?.rate })}
                      {' · '}
                      {t('pages.calendar.childCount', { count: entry.booking?.numberOfChildren ?? 0 })}
                    </div>
                  )}
                </div>
              ) : (
                <div className='leading-tight'>
                  <div className='font-semibold truncate'>
                    {t(bookingStatusKey(entry.booking))}
                  </div>
                  {height >= 28 && (
                    <div className='text-[10px] opacity-75 truncate'>
                      {timeLine}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className='leading-tight'>
                <div className='font-semibold truncate'>{t('pages.calendar.available')}</div>
                {height >= 28 && (
                  <div className='text-[10px] opacity-75 truncate'>
                    {timeLine}
                  </div>
                )}
              </div>
            )}
          </Button>
        );
      })}

      {dragBand && (
        <div
          className='absolute left-0.5 right-0.5 rounded bg-sapphire/30 border border-sapphire pointer-events-none'
          style={{ top: dragBand.top, height: dragBand.height }}
        />
      )}

      {todayMinutes >= 0 && (
        <div
          className='absolute left-0 right-0 pointer-events-none'
          style={{ top: pxFromMinutes(todayMinutes) }}
        >
          <div className='h-0.5 bg-red-500' />
          <div className='w-2 h-2 -mt-1 rounded-full bg-red-500 -ml-1' />
        </div>
      )}
    </div>
  );
};
